import { Fragment } from "react";

import { AnimatedArea, AnimatedAreaProps, AnimatedLine, AnimatedLineProps } from "@mui/x-charts/LineChart";
import { useChartId, useDrawingArea, useXScale } from "@mui/x-charts/hooks";

import { PROJECTION_DOT } from "./constants";
import { useProjectionStartTime } from "./util";

/**
 * Always renders. When `enableProjection` is false (or `now` is outside the x-axis domain),
 * the history clip covers the full drawing area and the projection clip collapses to zero width,
 * so consumers can render two copies unconditionally and get correct behavior either way.
 */
export default function ChartsProjectionDefs({ enableProjection }: { enableProjection?: boolean }) {
    const { top, bottom, height, left, width } = useDrawingArea();
    const { historyClipId, projectionClipId, dotPatternId, dotMaskId } = useProjectionDefIds();
    const splitX = useProjectionSplitX();
    const { cell, radius } = PROJECTION_DOT;

    const fullHeight = top + height + bottom;
    const fullWidth = left + width;
    const right = left + width;
    const effectiveSplitX = enableProjection && splitX !== undefined ? splitX : right;

    return (
        <defs>
            <clipPath id={historyClipId}>
                <rect x={left} y={0} width={Math.max(0, effectiveSplitX - left)} height={fullHeight} />
            </clipPath>
            <clipPath id={projectionClipId}>
                <rect x={effectiveSplitX} y={0} width={Math.max(0, right - effectiveSplitX)} height={fullHeight} />
            </clipPath>
            <pattern id={dotPatternId} width={cell} height={cell} patternUnits="userSpaceOnUse">
                <circle cx={cell / 2} cy={cell / 2} r={radius} fill="white" />
            </pattern>
            <mask id={dotMaskId} maskUnits="userSpaceOnUse">
                <rect x={0} y={0} width={fullWidth} height={fullHeight} fill={`url(#${dotPatternId})`} />
            </mask>
        </defs>
    );
}

export function AreaWithProjection(props: AnimatedAreaProps) {
    const { historyClipId, projectionClipId, dotMaskId } = useProjectionDefIds();

    return (
        <Fragment>
            <g clipPath={`url(#${historyClipId})`}>
                <AnimatedArea {...props} />
            </g>
            <g clipPath={`url(#${projectionClipId})`} mask={`url(#${dotMaskId})`}>
                <AnimatedArea {...props} />
            </g>
        </Fragment>
    );
}

export function LineWithProjection(props: AnimatedLineProps) {
    const { historyClipId, projectionClipId } = useProjectionDefIds();

    return (
        <Fragment>
            <g clipPath={`url(#${historyClipId})`}>
                <AnimatedLine {...props} />
            </g>
            <g clipPath={`url(#${projectionClipId})`} style={{ strokeDasharray: "4 4", strokeLinecap: "round" }}>
                <AnimatedLine {...props} />
            </g>
        </Fragment>
    );
}

export function useProjectionDefIds() {
    const chartId = useChartId();
    return {
        historyClipId: `${chartId}-projection-history`,
        projectionClipId: `${chartId}-projection-future`,
        dotPatternId: `${chartId}-projection-dots`,
        dotMaskId: `${chartId}-projection-dot-mask`
    };
}

function useProjectionSplitX() {
    const scale = useXScale();

    // Freeze "now" per chart so splitX doesn't creep as time passes during re-renders.
    const { now } = useProjectionStartTime();

    return scale(now) as number | undefined;
}
